import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    LogOut,
    Calendar,
    FileText,
    Users,
    Filter,
    Download,
    FileEdit,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, getAdminToken, clearAdminToken, API } from "../../lib/api";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "../../components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleString("en-IN", {
            dateStyle: "medium",
            timeStyle: "short",
        });
    } catch {
        return iso;
    }
};

const pkgLabel = {
    disha: "Disha",
    niti: "Niti",
    samvad: "Samvad",
    not_sure: "Exploring",
};

const pkgColor = {
    disha: "bg-[#F3EFE9] text-[#1A5C53] border-[#E6DFD3]",
    niti: "bg-[#1A5C53]/10 text-[#1A5C53] border-[#1A5C53]/20",
    samvad: "bg-[#D4944C]/15 text-[#B27530] border-[#D4944C]/30",
    not_sure: "bg-[#E6DFD3] text-[#4A5A58] border-[#E6DFD3]",
};

const BOOKING_STATUSES = ["new", "contacted", "converted", "closed"];
const SUBMISSION_STATUSES = [
    "received",
    "payment_verified",
    "under_review",
    "report_ready",
    "plan_delivered",
    "completed",
];

export default function AdminDashboard() {
    const navigate = useNavigate();
    const token = getAdminToken();
    const client = adminApi(token);
    const [stats, setStats] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!token) {
            navigate("/admin");
            return;
        }
        (async () => {
            try {
                const [s, b, sub] = await Promise.all([
                    client.get("/admin/stats"),
                    client.get("/admin/bookings"),
                    client.get("/admin/submissions"),
                ]);
                setStats(s.data);
                setBookings(b.data);
                setSubmissions(sub.data);
            } catch (err) {
                if (err?.response?.status === 401) {
                    clearAdminToken();
                    navigate("/admin");
                } else {
                    toast.error("Failed to load dashboard.");
                }
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const logout = () => {
        clearAdminToken();
        navigate("/admin");
    };

    const updateBookingStatus = async (id, status) => {
        try {
            const { data } = await client.patch(
                `/admin/bookings/${id}/status`,
                { status },
            );
            setBookings((bs) => bs.map((b) => (b.id === id ? data : b)));
            toast.success("Status updated.");
        } catch {
            toast.error("Update failed.");
        }
    };

    const updateSubmissionStatus = async (id, status) => {
        try {
            const { data } = await client.patch(
                `/admin/submissions/${id}/status`,
                { status },
            );
            setSubmissions((ss) => ss.map((s) => (s.id === id ? data : s)));
            toast.success("Status updated.");
        } catch {
            toast.error("Update failed.");
        }
    };

    const download = async (path, filename) => {
        try {
            const res = await fetch(`${API}${path}`, {
                headers: { "X-Admin-Password": token },
            });
            if (!res.ok) throw new Error();
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Export failed.");
        }
    };

    const filteredSubs =
        filter === "all"
            ? submissions
            : submissions.filter((s) => s.package === filter);

    return (
        <div className="min-h-screen bg-[#F3EFE9]">
            <div className="bg-white border-b border-[#E6DFD3]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
                    <Link to="/" className="font-serif text-2xl text-[#0D1B1A]">
                        Dhan<span className="text-[#D4944C]">Nniti</span>
                        <span className="ml-3 text-xs uppercase tracking-[0.2em] text-[#758684]">
                            Admin
                        </span>
                    </Link>
                    <div className="flex items-center gap-5">
                        <Link
                            to="/admin/blog"
                            data-testid="admin-blog-link"
                            className="inline-flex items-center gap-2 text-sm text-[#4A5A58] hover:text-[#1A5C53]"
                        >
                            <FileEdit size={16} /> Blog
                        </Link>
                        <button
                            onClick={logout}
                            data-testid="admin-logout-btn"
                            className="inline-flex items-center gap-2 text-sm text-[#4A5A58] hover:text-[#1A5C53]"
                        >
                            <LogOut size={16} /> Logout
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
                <div className="mb-10">
                    <h1 className="font-serif text-4xl text-[#0D1B1A]">
                        Dashboard
                    </h1>
                    <p className="text-[#4A5A58] mt-1">
                        Bookings and questionnaire submissions at a glance.
                    </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
                    <StatCard
                        icon={<Calendar size={18} />}
                        label="Total Bookings"
                        value={stats?.bookings ?? "…"}
                    />
                    <StatCard
                        icon={<FileText size={18} />}
                        label="Total Submissions"
                        value={stats?.submissions ?? "…"}
                    />
                    <StatCard
                        icon={<Users size={18} />}
                        label="Disha"
                        value={stats?.by_package?.disha ?? "…"}
                    />
                    <StatCard
                        icon={<Users size={18} />}
                        label="Niti"
                        value={stats?.by_package?.niti ?? "…"}
                        accent
                    />
                    <StatCard
                        icon={<Users size={18} />}
                        label="Samvad"
                        value={stats?.by_package?.samvad ?? "…"}
                    />
                </div>

                <Tabs defaultValue="bookings" className="w-full">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <TabsList className="bg-white border border-[#E6DFD3] p-1 rounded-lg">
                            <TabsTrigger
                                value="bookings"
                                data-testid="tab-bookings"
                                className="data-[state=active]:bg-[#1A5C53] data-[state=active]:text-white px-4 py-2"
                            >
                                Bookings ({bookings.length})
                            </TabsTrigger>
                            <TabsTrigger
                                value="submissions"
                                data-testid="tab-submissions"
                                className="data-[state=active]:bg-[#1A5C53] data-[state=active]:text-white px-4 py-2"
                            >
                                Submissions ({submissions.length})
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    <TabsContent value="bookings" className="mt-6">
                        <div className="flex justify-end mb-3">
                            <button
                                onClick={() =>
                                    download(
                                        "/admin/export/bookings.csv",
                                        "dhannniti-bookings.csv",
                                    )
                                }
                                data-testid="export-bookings-csv"
                                className="inline-flex items-center gap-2 bg-white border border-[#E6DFD3] hover:border-[#1A5C53] text-[#1A5C53] rounded-md px-4 py-2 text-sm"
                            >
                                <Download size={14} /> Export CSV
                            </button>
                        </div>
                        <div className="bg-white border border-[#E6DFD3] rounded-xl overflow-hidden">
                            <Table>
                                <TableHeader className="bg-[#F3EFE9]/60">
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Phone</TableHead>
                                        <TableHead>Package</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Received</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {!loading && bookings.length === 0 && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                className="text-center text-[#758684] py-10"
                                            >
                                                No bookings yet.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {bookings.map((b) => (
                                        <TableRow
                                            key={b.id}
                                            data-testid={`booking-row-${b.id}`}
                                        >
                                            <TableCell className="font-medium text-[#0D1B1A]">
                                                {b.name}
                                            </TableCell>
                                            <TableCell>{b.email}</TableCell>
                                            <TableCell>{b.phone}</TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant="outline"
                                                    className={
                                                        pkgColor[b.package] ||
                                                        pkgColor.not_sure
                                                    }
                                                >
                                                    {pkgLabel[b.package] ||
                                                        b.package}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Select
                                                    value={b.status}
                                                    onValueChange={(v) =>
                                                        updateBookingStatus(b.id, v)
                                                    }
                                                >
                                                    <SelectTrigger
                                                        data-testid={`booking-status-${b.id}`}
                                                        className="h-8 text-xs w-36"
                                                    >
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {BOOKING_STATUSES.map(
                                                            (s) => (
                                                                <SelectItem
                                                                    key={s}
                                                                    value={s}
                                                                >
                                                                    {s.replace(
                                                                        /_/g,
                                                                        " ",
                                                                    )}
                                                                </SelectItem>
                                                            ),
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                            </TableCell>
                                            <TableCell className="text-sm text-[#758684]">
                                                {formatDate(b.created_at)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="submissions" className="mt-6">
                        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                            <div className="flex items-center gap-3">
                                <Filter size={16} className="text-[#758684]" />
                                <Select value={filter} onValueChange={setFilter}>
                                    <SelectTrigger
                                        className="w-48 bg-white border-[#E6DFD3]"
                                        data-testid="submissions-filter"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">
                                            All Packages
                                        </SelectItem>
                                        <SelectItem value="disha">Disha</SelectItem>
                                        <SelectItem value="niti">Niti</SelectItem>
                                        <SelectItem value="samvad">
                                            Samvad
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <button
                                onClick={() =>
                                    download(
                                        `/admin/export/submissions.csv${filter !== "all" ? `?package=${filter}` : ""}`,
                                        `dhannniti-submissions${filter !== "all" ? "-" + filter : ""}.csv`,
                                    )
                                }
                                data-testid="export-submissions-csv"
                                className="inline-flex items-center gap-2 bg-white border border-[#E6DFD3] hover:border-[#1A5C53] text-[#1A5C53] rounded-md px-4 py-2 text-sm"
                            >
                                <Download size={14} /> Export CSV
                            </button>
                        </div>
                        <div className="bg-white border border-[#E6DFD3] rounded-xl overflow-hidden">
                            <Table>
                                <TableHeader className="bg-[#F3EFE9]/60">
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Package</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Received</TableHead>
                                        <TableHead></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {!loading && filteredSubs.length === 0 && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                className="text-center text-[#758684] py-10"
                                            >
                                                No submissions yet.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {filteredSubs.map((s) => (
                                        <TableRow key={s.id}>
                                            <TableCell className="font-medium text-[#0D1B1A]">
                                                {s.name}
                                            </TableCell>
                                            <TableCell>{s.email}</TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant="outline"
                                                    className={pkgColor[s.package]}
                                                >
                                                    {pkgLabel[s.package]}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Select
                                                    value={s.status}
                                                    onValueChange={(v) =>
                                                        updateSubmissionStatus(
                                                            s.id,
                                                            v,
                                                        )
                                                    }
                                                >
                                                    <SelectTrigger
                                                        data-testid={`submission-status-${s.id}`}
                                                        className="h-8 text-xs w-44"
                                                    >
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {SUBMISSION_STATUSES.map(
                                                            (st) => (
                                                                <SelectItem
                                                                    key={st}
                                                                    value={st}
                                                                >
                                                                    {st.replace(
                                                                        /_/g,
                                                                        " ",
                                                                    )}
                                                                </SelectItem>
                                                            ),
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                            </TableCell>
                                            <TableCell className="text-sm text-[#758684]">
                                                {formatDate(s.created_at)}
                                            </TableCell>
                                            <TableCell>
                                                <Link
                                                    to={`/admin/submission/${s.id}`}
                                                    data-testid={`view-sub-${s.id}`}
                                                    className="text-[#1A5C53] hover:underline text-sm"
                                                >
                                                    View →
                                                </Link>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function StatCard({ icon, label, value, accent }) {
    return (
        <div
            className={`rounded-xl border p-5 ${
                accent
                    ? "bg-[#1A5C53] text-white border-[#1A5C53]"
                    : "bg-white border-[#E6DFD3]"
            }`}
        >
            <div
                className={`flex items-center gap-2 text-xs tracking-[0.15em] uppercase ${
                    accent ? "text-[#D4944C]" : "text-[#758684]"
                }`}
            >
                {icon} {label}
            </div>
            <div
                className={`font-serif text-4xl mt-3 ${
                    accent ? "text-white" : "text-[#0D1B1A]"
                }`}
            >
                {value}
            </div>
        </div>
    );
}
